/**
 * Parses `GET <providerBaseUrl>/provider` (docs/215-PROVIDER-LOGIN-SECURITY-OIDC.md §2.7,
 * §3c) — public, unauthenticated metadata a content provider serves so the app can show
 * *whose* login it is before the user types anything (anti-phishing), and so it can
 * discover whether that provider is `"contract"` or `"oidc"` without guessing.
 *
 * This file only parses; it is deliberately silent on what a caller should do when the
 * request itself fails (network error, 404 — a provider that predates this endpoint).
 * That's the caller's job: docs/215 §3a says a provider not serving `/provider` degrades
 * to today's `"contract"` assumption, which every provider already speaks (docs/094).
 */
import { z } from "zod";
import { ParseResult, err, ok } from "../core/result";
import { CatalogTerms, CatalogTermsSchema } from "../legal/catalogTerms";
import { ProviderAuthKind } from "./types";

export interface ProviderMetadataOidc {
  issuer: string;
  clientId: string;
  scopes: string[];
}

export interface ProviderMetadata {
  name: string;
  logoUrl?: string;
  termsUrl?: string;
  /**
   * A versioned, ACCEPTANCE-gated terms-of-usage declaration
   * (docs/297-PROVIDER-CATALOG-TERMS-ACCEPTANCE.md v0 — the doc's "provider-level"
   * scope, `providerTermsScope`) — distinct from `termsUrl` above, which is a plain,
   * unversioned "View terms" link shown on `/add-provider` with no acceptance record.
   * Applies to ALL of this provider's catalogs. Additive/optional (docs/108-safe) — a
   * provider that predates this field parses unchanged, `termsUrl` (if any) still shown.
   */
  terms?: CatalogTerms;
  authKind: ProviderAuthKind;
  /** Present only when `authKind === "oidc"` — the discovery issuer + the client id
   * this provider has registered for Be Equipped, plus its requested scopes. */
  oidc?: ProviderMetadataOidc;
  /**
   * Additive (docs/292-PROVIDER-RETURNING-USER-APPLE-IDENTITY.md v0 §7) — which
   * RETURNING-user sign-in methods this provider offers beyond the invite-only
   * first-time flow: `"invite"`, `"apple"` (native Sign in with Apple, Option B),
   * later `"google"`. Absent/empty → the app shows only the invite path (today's
   * behavior) — a provider that predates this field degrades gracefully.
   */
  signInMethods?: string[];
  /**
   * Additive (docs/300-PROVIDER-DEVICE-ANALYTICS-AND-DISCLOSURE.md v0 §3.3/§3.4) —
   * present exactly when this provider has configured a device/usage tracking
   * disclosure. The hosted `/authorize` create step is where it's actually shown +
   * accepted (docs/296); this field is here so a client can discover it exists.
   * Absent → a provider that predates this field, or hasn't configured one, parses
   * unchanged.
   */
  trackingDisclosure?: { text?: string; url?: string; version: number };
  /**
   * Additive (docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md v0 §4/§8 Q5) — the
   * provider-level analog of docs/106's app-level `legalLinks`: plain "View …"
   * links, no acceptance/versioning (`termsUrl` above already covers the
   * unversioned terms link). Present exactly when at least one of
   * `privacyUrl`/`supportUrl`/`contentPolicyUrl` is configured.
   */
  legal?: { termsUrl?: string; privacyUrl?: string; supportUrl?: string; contentPolicyUrl?: string };
  /**
   * Additive (docs/332-MOBILE-ADMIN-APP-CROSS-PROVIDER.md v1 §4.2) — the
   * provider's own `contractVersion` string (this Worker's reference
   * implementation always sends it; a third-party/older provider may omit
   * it entirely). Feeds `@equipped/core/admin/adminCapabilities.ts`'s
   * `resolveAdminCapabilities` to semver-gate which admin operations the
   * admin app offers against THIS provider — absent means "legacy," treated
   * as an effective `0.0.0` baseline, never assumed current.
   */
  contractVersion?: string;
}

const TrackingDisclosureSchema = z.object({
  text: z.string().optional(),
  url: z.string().url().optional(),
  version: z.number(),
});

const ProviderLegalLinksSchema = z.object({
  termsUrl: z.string().url().optional(),
  privacyUrl: z.string().url().optional(),
  supportUrl: z.string().url().optional(),
  contentPolicyUrl: z.string().url().optional(),
});

const ProviderMetadataSchema = z
  .object({
    name: z.string().min(1),
    logoUrl: z.string().url().optional(),
    termsUrl: z.string().url().optional(),
    terms: CatalogTermsSchema.optional(),
    // `authKind` describes THIS provider's own primary auth mechanism (its `/token`/
    // session model) — unrelated to `signInMethods` below, which is an ADDITIONAL,
    // orthogonal axis of native relay methods (a provider is still fundamentally
    // "contract"-shaped even when it also offers a native Apple relay, docs/292 v0).
    authKind: z.enum(["contract", "oidc"]),
    oidc: z
      .object({
        issuer: z.string().url(),
        clientId: z.string().min(1),
        scopes: z.array(z.string()),
      })
      .optional(),
    signInMethods: z.array(z.string()).optional(),
    trackingDisclosure: TrackingDisclosureSchema.optional(),
    legal: ProviderLegalLinksSchema.optional(),
    contractVersion: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.authKind === "oidc" && !value.oidc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["oidc"],
        message: "oidc config is required when authKind is \"oidc\"",
      });
    }
  });

/** Parses a `GET /provider` response body. `ParseResult` — this is a live network
 * response, not local storage, so a malformed body is a real failure the caller must
 * see, same convention as `providers/types.ts`'s `parseCatalogsResponse`. */
export function parseProviderMetadata(json: unknown): ParseResult<ProviderMetadata> {
  const parsed = ProviderMetadataSchema.safeParse(json);
  if (!parsed.success) {
    return err(parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`));
  }
  const p = parsed.data;
  return ok({
    name: p.name,
    ...(p.logoUrl ? { logoUrl: p.logoUrl } : {}),
    ...(p.termsUrl ? { termsUrl: p.termsUrl } : {}),
    ...(p.terms ? { terms: p.terms } : {}),
    authKind: p.authKind,
    ...(p.oidc ? { oidc: p.oidc } : {}),
    ...(p.signInMethods && p.signInMethods.length > 0 ? { signInMethods: p.signInMethods } : {}),
    ...(p.trackingDisclosure ? { trackingDisclosure: p.trackingDisclosure } : {}),
    ...(p.legal ? { legal: p.legal } : {}),
    ...(p.contractVersion ? { contractVersion: p.contractVersion } : {}),
  });
}
